import { lazy, type ReactNode } from 'react';
import {
  Activity,
  Bookmark,
  Eye,
  Film,
  Gauge,
  History,
  Home,
  Layers,
  LayoutTemplate,
  Megaphone,
  Plus,
  Sparkles,
  Tag,
  Upload,
  User,
} from 'lucide-react';

import type { Action } from '@/config/access';

/** Every page is its own chunk, fetched the first time its route renders. */
const CampaignAssetsPage = lazy(() => import('@/pages/campaigns/campaign/CampaignAssetsPage').then((module) => ({ default: module.CampaignAssetsPage })));
const CampaignOverviewPage = lazy(() => import('@/pages/campaigns/campaign/CampaignOverviewPage').then((module) => ({ default: module.CampaignOverviewPage })));
const CreativeStudioPage = lazy(() => import('@/pages/campaigns/campaign/CreativeStudioPage').then((module) => ({ default: module.CreativeStudioPage })));
const AdvertisementAssetsPage = lazy(() => import('@/pages/campaigns/monitoring/AdvertisementAssetsPage').then((module) => ({ default: module.AdvertisementAssetsPage })));
const MonitoringPage = lazy(() => import('@/pages/campaigns/monitoring/MonitoringPage').then((module) => ({ default: module.MonitoringPage })));
const PublishingPage = lazy(() => import('@/pages/campaigns/monitoring/PublishingPage').then((module) => ({ default: module.PublishingPage })));
const VisualAnalysisPage = lazy(() => import('@/pages/campaigns/monitoring/VisualAnalysisPage').then((module) => ({ default: module.VisualAnalysisPage })));
const BrandCreatePage = lazy(() => import('@/pages/brandHub/brand/BrandCreatePage').then((module) => ({ default: module.BrandCreatePage })));
const BrandPage = lazy(() => import('@/pages/brandHub/brand/BrandPage').then((module) => ({ default: module.BrandPage })));
const BrandsPage = lazy(() => import('@/pages/brandHub/brand/BrandsPage').then((module) => ({ default: module.BrandsPage })));
const CampaignFormPage = lazy(() => import('@/pages/campaigns/campaign/CampaignFormPage').then((module) => ({ default: module.CampaignFormPage })));
const CampaignsPage = lazy(() => import('@/pages/campaigns/campaign/CampaignsPage').then((module) => ({ default: module.CampaignsPage })));
const DashboardPage = lazy(() => import('@/pages/DashboardPage').then((module) => ({ default: module.DashboardPage })));
const ProductImportPage = lazy(() => import('@/pages/brandHub/brand/ProductImportPage').then((module) => ({ default: module.ProductImportPage })));
const ProductImportsPage = lazy(() => import('@/pages/brandHub/brand/ProductImportsPage').then((module) => ({ default: module.ProductImportsPage })));
const SavedElementsPage = lazy(() => import('@/pages/brandHub/savedElements/SavedElementsPage').then((module) => ({ default: module.SavedElementsPage })));
const SettingsPage = lazy(() => import('@/pages/SettingsPage').then((module) => ({ default: module.SettingsPage })));
const TemplateFormPage = lazy(() => import('@/pages/brandHub/templates/TemplateFormPage').then((module) => ({ default: module.TemplateFormPage })));
const TemplatesPage = lazy(() => import('@/pages/brandHub/templates/TemplatesPage').then((module) => ({ default: module.TemplatesPage })));

export interface AppRoute {
  path: string;
  end?: boolean;
  labelKey: string;
  titleKey: string;
  subtitleKey?: string;
  icon: ReactNode;
  element: ReactNode;
  /** Gated by `ACCESS` in config/access.ts. No action means everyone. */
  action?: Action;
}

const ICON_SIZE = 16;

export const WORKSPACE_ROUTES: AppRoute[] = [
  {
    path: '/dashboard',
    end: true,
    labelKey: 'nav.dashboard',
    titleKey: 'dashboard.title',
    icon: <Gauge size={ICON_SIZE} />,
    element: <DashboardPage />,
  },
  {
    path: '/campaigns',
    end: true,
    labelKey: 'nav.campaigns',
    titleKey: 'campaigns.title',
    icon: <Megaphone size={ICON_SIZE} />,
    element: <CampaignsPage />,
  },
];

export const BRAND_HUB_ROUTES: AppRoute[] = [
  {
    path: '/brand-hub',
    end: true,
    labelKey: 'nav.brands',
    titleKey: 'brandHub.brands.title',
    subtitleKey: 'brandHub.brands.subtitle',
    icon: <Tag size={ICON_SIZE} />,
    element: <BrandsPage />,
    action: 'brandsPage',
  },
  {
    path: '/brand-hub/templates',
    end: true,
    labelKey: 'nav.templates',
    titleKey: 'brandHub.template.title',
    subtitleKey: 'brandHub.template.subtitle',
    icon: <LayoutTemplate size={ICON_SIZE} />,
    element: <TemplatesPage />,
    action: 'templatesPage',
  },
  {
    path: '/brand-hub/saved-elements',
    labelKey: 'nav.savedElements',
    titleKey: 'brandHub.savedElement.title',
    subtitleKey: 'brandHub.savedElement.subtitle',
    icon: <Bookmark size={ICON_SIZE} />,
    element: <SavedElementsPage />,
    action: 'savedElementsPage',
  },
];

export interface CampaignSectionRoute extends AppRoute {
  groupLabelKey?: string;
}

export const CAMPAIGN_SECTION_ROUTES: CampaignSectionRoute[] = [
  {
    path: 'overview',
    labelKey: 'nav.section.overview',
    titleKey: 'campaign.overview.title',
    icon: <Home size={ICON_SIZE} />,
    element: <CampaignOverviewPage />,
  },
  {
    path: 'assets',
    labelKey: 'nav.section.assets',
    titleKey: 'campaign.assets.title',
    icon: <Layers size={ICON_SIZE} />,
    element: <CampaignAssetsPage />,
  },
  {
    path: 'studio',
    labelKey: 'nav.section.studio',
    titleKey: 'campaign.studio.title',
    icon: <Sparkles size={ICON_SIZE} />,
    element: <CreativeStudioPage />,
  },
  {
    path: 'adassets',
    groupLabelKey: 'nav.section.operations',
    labelKey: 'nav.section.adassets',
    titleKey: 'campaign.adassets.title',
    icon: <Film size={ICON_SIZE} />,
    element: <AdvertisementAssetsPage />,
  },
  {
    path: 'publishing',
    labelKey: 'nav.section.publishing',
    titleKey: 'campaign.publishing.title',
    icon: <Upload size={ICON_SIZE} />,
    element: <PublishingPage />,
  },
  {
    path: 'visual',
    labelKey: 'nav.section.visual',
    titleKey: 'campaign.visual.title',
    icon: <Eye size={ICON_SIZE} />,
    element: <VisualAnalysisPage />,
  },
  {
    path: 'monitoring',
    labelKey: 'nav.section.monitoring',
    titleKey: 'campaign.monitoring.title',
    icon: <Activity size={ICON_SIZE} />,
    element: <MonitoringPage />,
  },
];

export const UNLISTED_ROUTES: AppRoute[] = [
  {
    path: '/campaigns/new',
    labelKey: 'campaigns.create',
    titleKey: 'campaignForm.createTitle',
    icon: <Megaphone size={ICON_SIZE} />,
    element: <CampaignFormPage mode="create" />,
  },
  {
    path: '/campaigns/:campaignId/edit',
    labelKey: 'campaigns.edit',
    titleKey: 'campaignForm.editTitle',
    icon: <Megaphone size={ICON_SIZE} />,
    element: <CampaignFormPage mode="edit" />,
  },
  {
    path: '/brand-hub/brand/new',
    labelKey: 'brandHub.brandCreate.title',
    titleKey: 'brandHub.brandCreate.title',
    subtitleKey: 'brandHub.brandCreate.subtitle',
    icon: <Plus size={ICON_SIZE} />,
    element: <BrandCreatePage />,
    action: 'brandManage',
  },
  {
    path: '/brand-hub/brand/:brandSlug',
    labelKey: 'brandHub.brand.title',
    titleKey: 'brandHub.brand.title',
    icon: <Tag size={ICON_SIZE} />,
    element: <BrandPage />,
    action: 'brandsPage',
  },
  {
    path: '/brand-hub/brand/:brandSlug/:tab',
    labelKey: 'brandHub.brand.title',
    titleKey: 'brandHub.brand.title',
    icon: <Tag size={ICON_SIZE} />,
    element: <BrandPage />,
    action: 'brandsPage',
  },
  {
    path: '/brand-hub/brand/:brandSlug/products/import',
    labelKey: 'brandHub.import.title',
    titleKey: 'brandHub.import.title',
    icon: <Upload size={ICON_SIZE} />,
    element: <ProductImportPage />,
    action: 'brandManage',
  },
  {
    path: '/brand-hub/brand/:brandSlug/products/imports',
    labelKey: 'brandHub.import.history.title',
    titleKey: 'brandHub.import.history.title',
    subtitleKey: 'brandHub.import.history.subtitle',
    icon: <History size={ICON_SIZE} />,
    element: <ProductImportsPage />,
    action: 'brandsPage',
  },
  {
    path: '/brand-hub/templates/new',
    labelKey: 'brandHub.template.createTitle',
    titleKey: 'brandHub.template.createTitle',
    subtitleKey: 'brandHub.template.addHint',
    icon: <LayoutTemplate size={ICON_SIZE} />,
    element: <TemplateFormPage />,
    action: 'templateCreate',
  },
  {
    path: '/brand-hub/templates/:templateId',
    labelKey: 'brandHub.template.editTitle',
    titleKey: 'brandHub.template.editTitle',
    icon: <LayoutTemplate size={ICON_SIZE} />,
    element: <TemplateFormPage />,
    action: 'templatesPage',
  },
  {
    path: '/settings',
    labelKey: 'nav.settings',
    titleKey: 'settings.title',
    icon: <User size={ICON_SIZE} />,
    element: <SettingsPage />,
  },
];

export const DEFAULT_ROUTE = '/campaigns';

export const NAV_GROUPS = [
  { key: 'brandHub', labelKey: 'nav.group.brandHub', routes: BRAND_HUB_ROUTES },
  { key: 'workspace', labelKey: 'nav.group.workspace', routes: WORKSPACE_ROUTES },
] as const;
